<template>
  <div class="c-search-on-click-with-suggestions relative">
    <CButton
      v-if="inputFocused || searchValue"
      class="c-search-on-click-with-suggestions__button absolute top-1 left-1 z-5 pa-2 box-border"
      :disabled="disabled"
      size="small"
      type="primary"
      @click.prevent="onSearchButtonClick"
    >
      <template #icon> <CIcon icon="search" /> </template>
    </CButton>

    <NAutoComplete
      v-model:value="searchValue"
      class="c-search-on-click-with-suggestions__auto-complete max-h-[36px] border-text3 border-solid rounded-1 box-border"
      :class="{ 'max-w-[320px] min-w-[280px]': !fullWidth, 'w-full': fullWidth, 'pl-8': inputFocused || searchValue }"
      :clearable="!!searchValue"
      :disabled="disabled"
      :getShow="() => showOptions"
      :menu-props="{
        class: 'min-w-[350px]! shadow-[0_4px_8px_rgba(0,0,0,0.20)]! -left-[35px]',
      }"
      :options="autocompleteOptions"
      :placeholder="placeholder"
      :renderLabel="renderLabel"
      :show="showOptions"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown.enter.capture="handleEnter"
      @select="handleOptionSelect"
      @update:value="handleAutoCompleteInput"
    >
      <template #suffix>
        <NSpin v-if="isLoading" size="small" />
      </template>
    </NAutoComplete>
  </div>
</template>

<script setup lang="tsx">
import { useI18n } from 'vue-i18n'
import { NAutoComplete, NSpin } from 'naive-ui'
import { nextTick, ref } from 'vue'
import { debounce } from 'lodash-es'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import type { StyledAutoCompleteOption } from '@lib/types/search'
import { SEARCH_TERM_PREFIX } from '@lib/composables/useMetadataSearch'
const props = withDefaults(
  defineProps<{
    disabled?: boolean
    placeholder?: string
    fullWidth?: boolean
    initialSearchValue?: string
    suggestionsFetcher: (value: string) => Promise<StyledAutoCompleteOption[]>
    minSearchLength?: number
  }>(),
  {
    initialSearchValue: '',
    minSearchLength: 3,
  },
)

const emit = defineEmits<{
  search: [s: string]
}>()

const { t } = useI18n()

const inputFocused = ref(false)
const searchValue = ref(props.initialSearchValue)
const autocompleteOptions = ref<StyledAutoCompleteOption[]>([])
const showOptions = ref(false)
const hasActiveSearch = ref(!!props.initialSearchValue)
const isLoading = ref(false)
const isLoadingSuggestions = ref(false)
const keepSuggestionsHidden = ref(false)

function clear() {
  if (!searchValue.value?.length && !hasActiveSearch.value) return

  searchValue.value = ''
  autocompleteOptions.value = []
  showOptions.value = false

  if (hasActiveSearch.value) {
    nextTick(() => {
      onSearch()
    })
  }

  hasActiveSearch.value = false
}

function handleEnter(event: Event) {
  if (searchValue.value && !showOptions.value) {
    event.stopPropagation()
    event.preventDefault()
    onSearch()

    if (isLoadingSuggestions.value) {
      keepSuggestionsHidden.value = true
    }
  }
}

function setValue(value: string) {
  searchValue.value = value
}

function handleBlur(e: FocusEvent) {
  const relatedElement = e.relatedTarget as HTMLElement | null
  // only way to catch click on icon button while focusing out
  if (relatedElement?.classList?.contains('c-search-on-click-with-suggestions__button')) return

  inputFocused.value = false
  showOptions.value = false
}

function handleFocus() {
  inputFocused.value = true
  if (searchValue.value && searchValue.value.length >= props.minSearchLength) {
    fetchOptions(searchValue.value)
  }
}

function handleAutoCompleteInput(value: string) {
  if (!value) {
    clear()
    inputFocused.value = false
    isLoading.value = false
    return
  }

  if (value && value.length >= props.minSearchLength) {
    isLoading.value = true
  }

  showOptions.value = false
  autocompleteOptions.value = []
  isLoadingSuggestions.value = true
  debouncedFetchOptions(value)
}

const debouncedFetchOptions = debounce(async (value: string) => {
  try {
    if (!value) {
      clear()
      return
    }

    if (value.length >= props.minSearchLength) {
      await fetchOptions(value)
    } else {
      autocompleteOptions.value = []
      showOptions.value = false
      isLoading.value = false
    }
  } finally {
    isLoadingSuggestions.value = false
  }
}, 500)

async function fetchOptions(value: string) {
  try {
    autocompleteOptions.value = []

    const results = await props.suggestionsFetcher(value)

    if (results && Array.isArray(results)) {
      autocompleteOptions.value = results.map((item) => ({
        label: item.label,
        value: String(item.value),
        style: item.style,
        isTypedSearchQuery: item.isTypedSearchQuery,
      }))

      if (!keepSuggestionsHidden.value) {
        // Ensure options are shown when we have results
        showOptions.value = autocompleteOptions.value.length > 0
      }
    } else {
      autocompleteOptions.value = []
      showOptions.value = false
    }
  } catch (error) {
    autocompleteOptions.value = []
    showOptions.value = false
  } finally {
    isLoading.value = false
    keepSuggestionsHidden.value = false
  }
}

function handleOptionSelect(value: string) {
  // Check if this is a search term option using the isTypedSearchQuery property
  const selectedOption = autocompleteOptions.value.find((option) => option.value === value)
  if (selectedOption?.isTypedSearchQuery) {
    // Extract the actual search term from the unique value
    const actualSearchTerm = value.startsWith(SEARCH_TERM_PREFIX) ? value.replace(SEARCH_TERM_PREFIX, '') : value
    searchValue.value = actualSearchTerm
  } else {
    searchValue.value = value
  }
  onSearch()
}

function onSearchButtonClick() {
  if (!searchValue.value) {
    inputFocused.value = false
  }
  onSearch()
}

function onSearch() {
  hasActiveSearch.value = true
  emit('search', searchValue.value)
}

const renderLabel = (option: StyledAutoCompleteOption) => {
  let label = option.label
  if (option.isTypedSearchQuery) {
    label = `${t('canvasUI.CSearchOnClickWithSuggestions.searchFor')}: ${option.label}`
  }
  return (
    <div class="truncate" style={option.style}>
      {label}
    </div>
  )
}

defineExpose({
  clear,
  setValue,
})
</script>

<style scoped>
.c-search-on-click-with-suggestions__auto-complete :deep(.n-input__border),
.c-search-on-click-with-suggestions__auto-complete :deep(.n-input__state-border),
.c-search-on-click-with-suggestions__auto-complete :deep(.n-input) {
  border: none !important;
}

.c-search-on-click-with-suggestions__auto-complete :deep(.n-base-loading__container),
.c-search-on-click-with-suggestions__auto-complete :deep(.n-base-loading),
.c-search-on-click-with-suggestions__auto-complete :deep(.n-base-loading__container svg) {
  width: 12px;
  height: 12px;
}
</style>
